// app/components/privacy-graph-scrolly/elements/peripheral-card.tsx
//
// Smaller, low-emphasis card used in beats 1–2 to show that the scan covers
// more than just the focused workload. Renders as a single-row card with
// icon + name, no resource id, lower opacity. Fades out during beat 3 settle.

import {
  Activity,
  Bell,
  Boxes,
  Database,
  GitBranch,
  Key,
  Mail,
  MessageCircle,
  UserCheck,
  Waves,
  Workflow,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { SVG_COLORS } from "@/lib/svg-colors";

const PERIPHERAL_W = 120;
const PERIPHERAL_H = 32;
const RADIUS = 5;
const PADDING_X = 8;
const ICON_SIZE = 14;
const ICON_GAP = 6;

const ICON_MAP: Record<string, LucideIcon> = {
  rds: Database,
  "cw-logs": Activity,
  ecs: Boxes,
  sqs: MessageCircle,
  cognito: UserCheck,
  eventbridge: GitBranch,
  secrets: Key,
  ses: Mail,
  kinesis: Waves,
  "step-fns": Workflow,
  glue: Wrench,
  sns: Bell,
};

interface Props {
  id: string;
  label: string;
  /** Top-left x in SVG coordinates. */
  x: number;
  /** Top-left y in SVG coordinates. */
  y: number;
  opacity?: number;
}

export function PeripheralCard({ id, label, x, y, opacity = 1 }: Props) {
  const Icon = ICON_MAP[id] ?? Database;
  const iconY = (PERIPHERAL_H - ICON_SIZE) / 2;
  const labelX = PADDING_X + ICON_SIZE + ICON_GAP;
  // Optical-center 11px text against icon midline.
  const labelBaseY = iconY + ICON_SIZE / 2 + 4;

  return (
    <g transform={`translate(${x}, ${y})`} opacity={opacity}>
      <rect
        width={PERIPHERAL_W}
        height={PERIPHERAL_H}
        rx={RADIUS}
        fill={SVG_COLORS.surface}
        stroke={SVG_COLORS.border}
        strokeWidth={1}
      />
      <g transform={`translate(${PADDING_X}, ${iconY})`}>
        <Icon
          width={ICON_SIZE}
          height={ICON_SIZE}
          color={SVG_COLORS.neutral600}
          strokeWidth={1.75}
          aria-hidden
        />
      </g>
      <text
        x={labelX}
        y={labelBaseY}
        fontSize={12}
        fontWeight={500}
        fill={SVG_COLORS.neutral600}
        fontFamily="var(--font-sans, system-ui, sans-serif)"
      >
        {label}
      </text>
    </g>
  );
}

export const PERIPHERAL_CARD_W = PERIPHERAL_W;
export const PERIPHERAL_CARD_H = PERIPHERAL_H;
